import { Chip, Grid, IconButton, TextField, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import moment from 'moment/moment';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { AutoAwesome, Check, Edit, Email, School } from '@mui/icons-material';
import { getEmployeeNames } from '../../../CalendarUtils';
import { WorkshopCircle } from '../../../../common/ui/WorkshopCircle';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { AccountData, EnrichedEventData, EventService, OrderData } from '../../../../api';
import { useLanguage } from '../../../../common/GeneralUtils';
import { useCalendar } from '../../../CalendarContext/CalendarContext';
import { getOrderPage } from '../../../../utils/AccountUtils';
import { getDateFormat, getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import { useEvents } from '../../../swr/useEvents';
import { useCourse } from '../../../swr/useCourse';
import RotatingLoader from '../../../../common/ui/RotatingLoader';
import { useEventPassHolders } from '../../../swr/useEventPassHolders';
import { eventSupportsPassHolds } from '../passHoldsVisibility';
import {
  activeTickets,
  findMultipleTicketsUiObject,
  getCountingUsedTickets,
  readMultipleTicketsQuantities,
} from '../../../../common/multipleTicketsUtils';

export const calculateTotalCheckedInPersons = (orderList: OrderData[]) => {
  return isEmpty(orderList)
    ? 0
    : orderList.reduce((totalPersons, item) => {
        if (item.checkin && item.checkin.persons) {
          return totalPersons + item.checkin.persons;
        }
        return totalPersons;
      }, 0);
};

export const calculateTotalMissingPersons = (orderList: OrderData[]) =>
  orderList.reduce((totalPersons, item) => totalPersons + Math.max(item.persons - (item.checkin?.persons || 0), 0), 0);

// One cell per fact. The widths turn the header into columns as the dialog gets wider, so a dozen
// facts cost one or two rows instead of a dozen -- the tall stack used to push the participants
// table most of the way off the screen. Gutters are padding, not Grid `spacing`, because spacing's
// negative margins bleed out of the dialog's clipped header.
const StatCell = ({ children }: { children: ReactNode }) => (
  <Grid item xs={12} sm={6} md={4} lg={3} sx={{ paddingInlineEnd: '16px', pb: 0.25 }}>
    {children}
  </Grid>
);

type EventBoxDialogInfoHeaderProps = {
  event: EnrichedEventData;
  ordersList: OrderData[];
  isLoading: boolean;
};
export const EventBoxDialogInfoHeader = ({ event, ordersList, isLoading }: EventBoxDialogInfoHeaderProps) => {
  const { users } = useCalendar();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);
  const { longDateFormat } = getDateFormat(account);
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [internalComment, setInternalComment] = useState(event.comment || '');
  const [sendToManager, setSendToManager] = useState(false);
  const { courseData, isCourseLoading } = useCourse(event.course_id);
  const { startDate, endDate } = useCalendar();
  const { reFetch: refetchEvents } = useEvents({ startDate, endDate });
  const handleSaveComment = async () => {
    try {
      await EventService.updateEventComment({
        event_id: event.id,
        comment: internalComment,
        send_to_manager: sendToManager,
      });
      void refetchEvents();
      setIsEditingComment(false);
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
  };
  const language = useLanguage();
  const orderPage = getOrderPage(account, event.order_page_id);
  const { t } = useTranslation();
  // global setting doesnt seem to obe propely updated and refreshed on lang change
  moment.locale(language);
  const orderPageCustomData = orderPage.custom_details;
  const customUsages = event.custom_ticket_usages;
  const customUsedTickets = customUsages?.reduce((total, usage) => total + usage.used_tickets, 0) || 0;
  const usedTickets = getCountingUsedTickets(event, orderPage) + customUsedTickets;
  const {
    holders: passHolders,
    weeklyHoldersCount,
    slotCapacity,
  } = useEventPassHolders({
    eventId: event.id,
    enabled: eventSupportsPassHolds(account, event.order_page_id),
  });
  const signedOffHoldersCount = passHolders.filter((holder) => holder.canceled_booking).length;

  const multipleTicketsUi = findMultipleTicketsUiObject(orderPage);
  const multipleTicketsSummary = multipleTicketsUi
    ? activeTickets(multipleTicketsUi).map((ticket) => {
        const sum = ordersList.reduce((total, order) => {
          const quantities = readMultipleTicketsQuantities(order.custom_details as Record<string, any> | undefined);
          return total + (quantities[ticket.id] || 0);
        }, 0);
        return {
          id: ticket.id,
          label: ticket.label?.[language] || ticket.id,
          sum,
        };
      })
    : [];
  const accountLocations = account.locations;
  const isCourseSession = !!event.course_id && !!courseData;
  const sessionIndex = courseData?.sessions?.findIndex((s) => s.date === event.date && s.time === event.time) ?? -1;
  const sessionNumber = sessionIndex >= 0 ? sessionIndex + 1 : null;
  const totalSessions = courseData?.sessions?.length ?? 0;
  const showPassHolds = passHolders.length > 0 || weeklyHoldersCount > 0;
  const location = accountLocations.find((x) => x.id === event.location_id)?.name?.[language];

  if (isCourseLoading) {
    return (
      <Grid container alignItems="center" justifyContent="center" p={3}>
        <RotatingLoader />
      </Grid>
    );
  }

  return (
    <Grid container alignItems="flex-start" flexDirection="column" flexWrap="nowrap" mx={1}>
      <Grid container mb={0.5} alignItems="center" gap={1}>
        <WorkshopCircle account={account} language={language} orderPageId={orderPage.id} />
        <Typography variant="h5">{orderPage.label[language]}</Typography>
        {isCourseSession && sessionNumber && (
          <Chip
            icon={<School />}
            label={t('calendar.course_session.session_label', {
              current: sessionNumber,
              total: totalSessions,
            })}
            size="small"
            sx={{ bgcolor: 'secondary.light', color: 'secondary.dark', '& .MuiChip-icon': { color: 'secondary.dark' } }}
          />
        )}
        {event.generated_from_working_days && (
          <Tooltip title={t('eventSchedule.autoGeneratedTooltip')} arrow>
            <Chip
              icon={<AutoAwesome />}
              label={t('eventSchedule.autoGeneratedBadge')}
              size="small"
              sx={{ bgcolor: 'primary.light', color: 'primary.main', '& .MuiChip-icon': { color: 'primary.main' } }}
            />
          </Tooltip>
        )}
      </Grid>

      {/* Location and the date/time share a line rather than stacking into two. */}
      <Grid container alignItems="center" columnGap={2} rowGap={0.5} mb={0.5}>
        {accountLocations.length > 1 && location && (
          <Typography variant="body1" color="text.secondary" fontWeight={700}>
            {location}
          </Typography>
        )}
        <Typography variant="body1" color="text.secondary" fontWeight={700}>
          {moment(event.date).format(longDateFormat)} ⋅ {moment.unix(event.time).utc().format(timeFormat)}-
          {moment
            .unix(event.time + event.duration)
            .utc()
            .format(timeFormat)}
        </Typography>
      </Grid>

      <Grid container>
        <StatCell>
          <Typography variant="body1" color="text.secondary">
            {`${t('calendar.event_popper.participants')}:`} {usedTickets}/{event.tickets}
          </Typography>
        </StatCell>
        {showPassHolds && (
          <StatCell>
            <Typography variant="body1" color="text.secondary">
              {`${t('calendar.event_popper.pass_holds.held_by_passes')}: `}
              {passHolders.length}
              {signedOffHoldersCount > 0 &&
                ` ${t('calendar.event_popper.pass_holds.signed_off_suffix', {
                  count: signedOffHoldersCount,
                })}`}
            </Typography>
          </StatCell>
        )}
        {showPassHolds && (
          <StatCell>
            <Typography variant="body1" color={weeklyHoldersCount >= slotCapacity ? 'warning.main' : 'text.secondary'}>
              {`${t('calendar.event_popper.pass_holds.weekly_slot')}: `}
              {weeklyHoldersCount}/{slotCapacity}
              {weeklyHoldersCount >= slotCapacity &&
                ` · ${t('calendar.event_popper.pass_holds.no_room_for_new_pass_buyers')}`}
            </Typography>
          </StatCell>
        )}
        {multipleTicketsSummary.map((entry) => (
          <StatCell key={entry.id}>
            <Typography variant="body1" color="text.secondary">
              {`${entry.label}: `}
              {isLoading ? 0 : entry.sum}
            </Typography>
          </StatCell>
        ))}
        {customUsages?.map((customTicketUsage) => (
          <StatCell key={customTicketUsage.name}>
            <Typography variant="body1" color="text.secondary">
              {`${orderPageCustomData.find((x: any) => x.name === customTicketUsage.name)?.label.en} ${t(
                'calendar.event_popper.participants',
              )}: `}
              {customTicketUsage.used_tickets}/{customTicketUsage.tickets}
            </Typography>
          </StatCell>
        ))}
        <StatCell>
          <Typography variant="body1" color="text.secondary">
            {`${t('calendar.event_popper.numOfOrders')}:`} {isLoading ? 0 : ordersList.length}
          </Typography>
        </StatCell>
        <StatCell>
          <Typography variant="body1" color="text.secondary">
            {`${t('calendar.event_popper.numOfFreeTickets')}: `}
            {isLoading ? 0 : ordersList.filter((x) => x.price === 0).reduce((sum, item) => sum + item.persons, 0)}
          </Typography>
        </StatCell>
        <StatCell>
          <Typography variant="body1" color="text.secondary">
            {t('calendar.event_popper.total_checkeding', {
              total: isLoading ? 0 : calculateTotalCheckedInPersons(ordersList),
            })}
          </Typography>
        </StatCell>
        {event.employee_ids.length > 0 && (
          <StatCell>
            <Typography variant="body1" color="text.secondary">
              {`${t('calendar.event_popper.employees')} `}
              {getEmployeeNames(event.employee_ids, users).join(', ')}
            </Typography>
          </StatCell>
        )}
      </Grid>

      {isCourseSession && courseData && courseData.sessions && courseData.sessions.length > 0 && (
        <Grid container flexDirection="column" mt={1}>
          <Typography variant="body1" color="text.secondary" fontWeight={700}>
            {t('calendar.course_session.all_sessions')}
          </Typography>
          <Grid container>
            {courseData.sessions?.map((session, index) => {
              const isCurrentSession = session.date === event.date && session.time === event.time;
              const isPastSession = moment(session.date).isBefore(moment(), 'day');
              return (
                <StatCell key={index}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontWeight: isCurrentSession ? 700 : 400,
                      backgroundColor: isCurrentSession ? 'primary.light' : 'transparent',
                      borderRadius: '4px',
                      px: 0.5,
                      textDecoration: isPastSession ? 'line-through' : 'none',
                      opacity: isPastSession ? 0.6 : 1,
                    }}>
                    {t('calendar.course_session.session_item', {
                      number: index + 1,
                      date: moment(session.date).format(longDateFormat),
                      time: moment.unix(session.time).utc().format(timeFormat),
                    })}
                  </Typography>
                </StatCell>
              );
            })}
          </Grid>
        </Grid>
      )}

      <Grid item container alignItems="center" gap={1} mt={1}>
        {isEditingComment ? (
          <Grid container direction="column" gap={1}>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={internalComment}
              onChange={(e) => setInternalComment(e.target.value)}
              placeholder={t('calendar.event_popper.commentPlaceholder')}
              size="small"
            />
            <Grid container gap={1}>
              <IconButton
                size="small"
                onClick={() => handleSaveComment()}
                color={sendToManager ? 'primary' : 'default'}
                onMouseEnter={() => setSendToManager(true)}
                onMouseLeave={() => setSendToManager(false)}>
                <Tooltip title={t('calendar.event_popper.sendToManager')}>
                  <Email />
                </Tooltip>
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  setSendToManager(false);
                  void handleSaveComment();
                }}>
                <Tooltip title={t('calendar.event_popper.saveComment')}>
                  <Check />
                </Tooltip>
              </IconButton>
            </Grid>
          </Grid>
        ) : (
          <Grid container alignItems="center" gap={1}>
            <Typography variant="body1" color="text.secondary">
              {internalComment || t('calendar.event_popper.noComment')}
            </Typography>
            <IconButton size="small" onClick={() => setIsEditingComment(true)}>
              <Edit fontSize="small" />
            </IconButton>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
